import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Avatar,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material'
import { EmployeesMobileView } from './EmployeesMobileView'
import {
  Add,
  Search,
  Refresh,
  Edit,
  Delete,
  Visibility,
  Person,
  Work,
  Email,
  Phone,
  Schedule,
  CalendarToday,
  CalendarMonth,
  Badge,
  EventAvailable,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { MobileDataCard } from '../../components/Common/MobileDataCard'
import type { MobileDataCardAction, MobileDataCardField } from '../../components/Common/MobileDataCard'
import {
  fetchEmployees,
  setFilters,
  setPagination,
  deleteEmployee,
  createEmployee,
  updateEmployee,
} from '../../store/slices/employeesSlice'
import { useEmployeeUpdates } from '../../hooks/useWebSocket'
import { EmployeeDialog } from '../../components/Employees/EmployeeDialog'
import { EmployeeDetailsDialog } from '../../components/Employees/EmployeeDetailsDialog'
import { ConfirmDialog } from '../../components/Common/ConfirmDialog'
import { AttendanceManagementPageFinal } from '../attendance/AttendanceManagementPageFinal'
import EmployeeCalendarView from '../../components/Employees/EmployeeCalendarView'
import HolidayList from '../../components/Employees/HolidayList'

const DEPARTMENTS = [
  { value: '', label: 'All Departments' },
  { value: 'production', label: 'Production' },
  { value: 'quality_control', label: 'Quality Control' },
  { value: 'design', label: 'Design' },
  { value: 'management', label: 'Management' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'finance', label: 'Finance' },
  { value: 'maintenance', label: 'Maintenance' },
]

const EMPLOYEE_STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'on_leave', label: 'On Leave' },
  { value: 'terminated', label: 'Terminated' },
]

export const EmployeesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { employees, total, isLoading, filters, pagination } = useAppSelector((state) => state.employees)
  const { employees: realtimeEmployees } = useEmployeeUpdates()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  // Render mobile view for mobile devices
  if (isMobile) {
    return <EmployeesMobileView />
  }

  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null)
  const [currentTab, setCurrentTab] = useState(0)

  // Define employee type for better type safety
  interface Employee {
    id?: number
    employee_id: string
    first_name: string
    last_name: string
    full_name?: string
    email?: string
    phone?: string
    department: string
    position: string
    hourly_rate?: number
    salary?: number
    status: string
    is_active?: boolean
    hire_date: string
  }

  // Load employees on component mount and when filters change
  useEffect(() => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchEmployees(params))
  }, [dispatch, filters, pagination])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ search: value }))
    }, 500)

    return () => clearTimeout(timeoutId)
  }

  const handleDepartmentFilter = (department: string) => {
    dispatch(setFilters({ department: department || undefined }))
  }

  const handleStatusFilter = (status: string) => {
    dispatch(setFilters({ status: status || undefined }))
  }

  const handleRefresh = () => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchEmployees(params))
  }

  const handleCreateEmployee = () => {
    setSelectedEmployee(null)
    setEmployeeDialogOpen(true)
  }

  const handleEditEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setEmployeeDialogOpen(true)
  }

  const handleViewEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setDetailsDialogOpen(true)
  }

  const handleDeleteEmployee = (employee: any) => {
    setSelectedEmployee(employee)
    setConfirmDialogOpen(true)
  }

  const handleViewAttendance = (employee: any) => {
    setSelectedEmployee(employee)
    setCurrentTab(1) // Switch to attendance tab
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  const confirmDelete = async () => {
    if (selectedEmployee) {
      await dispatch(deleteEmployee(selectedEmployee.id || 0))
      setConfirmDialogOpen(false)
      setSelectedEmployee(null)
    }
  }

  const handleEmployeeSubmit = async (employeeData: any) => {
    try {
      if (selectedEmployee && selectedEmployee.id) {
        // Update existing employee
        await dispatch(updateEmployee({
          employeeId: selectedEmployee.id,
          employeeData
        })).unwrap()
      } else {
        // Create new employee
        await dispatch(createEmployee(employeeData)).unwrap()
      }
      setEmployeeDialogOpen(false)
      setSelectedEmployee(null)
      // Refresh the employee list
      handleRefresh()
    } catch (error) {
      console.error('Error submitting employee:', error)
      // Error handling is managed by Redux state
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'default'
      case 'on_leave':
        return 'warning'
      case 'terminated':
        return 'error'
      default:
        return 'default'
    }
  }

  const getDepartmentColor = (department: string) => {
    switch (department.toLowerCase()) {
      case 'production':
        return 'primary'
      case 'quality_control':
        return 'secondary'
      case 'design':
        return 'info'
      case 'management':
        return 'warning'
      case 'hr':
        return 'success'
      case 'finance':
        return 'error'
      default:
        return 'default'
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'employee_id',
      headerName: 'Employee ID',
      width: 110,
      minWidth: 100,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'full_name',
      headerName: 'Name',
      width: 180,
      minWidth: 150,
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {params.value.charAt(0)}
          </Avatar>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 180,
      minWidth: 150,
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 130,
      minWidth: 120,
      flex: 0.9,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 140,
      minWidth: 120,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getDepartmentColor(params.value) as any}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'position',
      headerName: 'Position',
      width: 140,
      minWidth: 120,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Work sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'salary',
      headerName: 'Salary',
      width: 110,
      minWidth: 100,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          Rs. {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      minWidth: 100,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      field: 'hire_date',
      headerName: 'Hire Date',
      width: 110,
      minWidth: 100,
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 160,
      minWidth: 160,
      flex: 0,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="View"
          onClick={() => handleViewEmployee(params.row)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleEditEmployee(params.row)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          icon={<Schedule />}
          label="Attendance"
          onClick={() => handleViewAttendance(params.row)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDeleteEmployee(params.row)}
          showInMenu={false}
        />,
      ],
    },
  ]

  // Render mobile card for an employee
  const renderMobileEmployeeCard = (employee: any) => {
    const fields: MobileDataCardField[] = [
      {
        label: 'Employee ID',
        value: employee.employee_id,
        icon: <Badge sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Email',
        value: employee.email || 'N/A',
        icon: <Email sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Phone',
        value: employee.phone || 'N/A',
        icon: <Phone sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Department',
        value: employee.department,
        variant: 'chip',
        chipColor: getDepartmentColor(employee.department) as any,
        chipVariant: 'outlined',
      },
      {
        label: 'Position',
        value: employee.position,
        icon: <Work sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Salary',
        value: `Rs. ${employee.salary?.toLocaleString()}`,
        variant: 'currency',
      },
      {
        label: 'Status',
        value: employee.status,
        variant: 'chip',
        chipColor: getStatusColor(employee.status) as any,
        chipVariant: 'filled',
      },
      {
        label: 'Hire Date',
        value: new Date(employee.hire_date).toLocaleDateString(),
        variant: 'date',
        icon: <CalendarToday sx={{ fontSize: 16 }} />,
      },
    ]

    const actions: MobileDataCardAction[] = [
      {
        label: 'View Details',
        icon: <Visibility />,
        onClick: () => handleViewEmployee(employee),
      },
      {
        label: 'Edit Employee',
        icon: <Edit />,
        onClick: () => handleEditEmployee(employee),
      },
      {
        label: 'View Attendance',
        icon: <Schedule />,
        onClick: () => handleViewAttendance(employee),
      },
      {
        label: 'Delete',
        icon: <Delete />,
        onClick: () => handleDeleteEmployee(employee),
        color: 'error',
      },
    ]

    return (
      <MobileDataCard
        key={employee.id || employee.employee_id}
        title={employee.full_name}
        subtitle={employee.position}
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            {employee.full_name.charAt(0)}
          </Avatar>
        }
        fields={fields}
        actions={actions}
        onClick={() => handleViewEmployee(employee)}
      />
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 3, md: 4 },
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            color: 'onSurface',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          Employee Management
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateEmployee}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              }
            }}
          >
            Add Employee
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{
        borderBottom: 1,
        borderColor: 'outlineVariant',
        mb: { xs: 3, md: 4 },
        backgroundColor: 'surfaceContainer',
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden'
      }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              minHeight: 56,
              px: 3,
              py: 2,
              borderRadius: 2,
              mx: 0.5,
              my: 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'surfaceVariant',
              },
              '&.Mui-selected': {
                backgroundColor: 'primaryContainer',
                color: 'onPrimaryContainer',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-flexContainer': {
              gap: 1,
            }
          }}
        >
          <Tab
            icon={<Person />}
            label="Employee List"
            iconPosition="start"
            sx={{ gap: 1.5 }}
          />
          <Tab
            icon={<CalendarToday />}
            label="Attendance Management"
            iconPosition="start"
            sx={{ gap: 1.5 }}
          />
          <Tab
            icon={<CalendarMonth />}
            label="Calendar View"
            iconPosition="start"
            sx={{ gap: 1.5 }}
          />
          <Tab
            icon={<EventAvailable />}
            label="Holidays"
            iconPosition="start"
            sx={{ gap: 1.5 }}
          />
        </Tabs>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{
        width: '100%',
        maxWidth: '100%',
        m: 0,
        mb: { xs: 3, md: 4 },
      }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    {total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    Total Employees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 24px rgba(17, 153, 142, 0.25)',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    {(employees || []).filter((emp: any) => emp.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    Active Employees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 24px rgba(240, 147, 251, 0.25)',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Work sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    {new Set((employees || []).map((emp: any) => emp.department)).size}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    Departments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%)',
            borderRadius: '20px',
            boxShadow: '0 8px 24px rgba(255, 107, 107, 0.25)',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    {(employees || []).filter((emp: any) => {
                      const hireDate = new Date(emp.hire_date)
                      const monthsAgo = new Date()
                      monthsAgo.setMonth(monthsAgo.getMonth() - 3)
                      return hireDate > monthsAgo
                    }).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    New Hires (3mo)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 3, md: 4 }
        }}>
          {/* Filters */}
          <Card sx={{
            backgroundColor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1,
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} alignItems="center">
                <Grid item xs={12} sm={12} md={5} lg={4}>
                  <TextField
                    fullWidth
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={handleSearch}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'text.primary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={filters.department || ''}
                      label="Department"
                      onChange={(e: any) => handleDepartmentFilter(e.target.value)}
                      sx={{
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'text.primary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      }}
                    >
                      {DEPARTMENTS.map((dept) => (
                        <MenuItem key={dept.value} value={dept.value}>
                          {dept.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status || ''}
                      label="Status"
                      onChange={(e: any) => handleStatusFilter(e.target.value)}
                      sx={{
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'text.primary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      }}
                    >
                      {EMPLOYEE_STATUSES.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={1} lg={2}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'center', md: 'center' },
                    width: '100%'
                  }}>
                    <Tooltip title="Refresh">
                      <IconButton
                        onClick={handleRefresh}
                        sx={{
                          backgroundColor: 'action.hover',
                          borderRadius: 2,
                          p: 1.5,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Mobile View - Card List */}
          {isMobile ? (
            <Stack spacing={2} sx={{ mb: 10 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography color="text.secondary">Loading employees...</Typography>
                </Box>
              ) : employees && employees.length > 0 ? (
                employees.map((employee: any) => renderMobileEmployeeCard(employee))
              ) : (
                <Card sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No employees found</Typography>
                </Card>
              )}
            </Stack>
          ) : (
            /* Desktop/Tablet View - Data Grid */
            <Card sx={{
              backgroundColor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 2,
              overflow: 'hidden',
              width: '100%',
            }}>
              <Box sx={{
                width: '100%',
                overflow: 'auto',
              }}>
                <DataGrid
                  rows={employees || []}
                  columns={columns}
                  getRowId={(row: any) => row.id || row.employee_id}
                  loading={isLoading}
                  paginationMode="server"
                  rowCount={total || 0}
                  paginationModel={{ page: pagination.page - 1, pageSize: pagination.limit }}
                  onPaginationModelChange={(model: any) => dispatch(setPagination({ page: model.page + 1, limit: model.pageSize }))}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  autoHeight
                  sx={{
                    border: 'none',
                    width: '100%',
                    minWidth: '100%',
                    '& .MuiDataGrid-main': {
                      backgroundColor: 'background.paper',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      minHeight: '400px',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'background.default',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      minHeight: { xs: 48, sm: 56 },
                    },
                    '& .MuiDataGrid-columnHeader': {
                      '&:focus': {
                        outline: 'none',
                      },
                      '&:focus-within': {
                        outline: 'none',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: { xs: 0.75, sm: 1 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      '&:focus': {
                        outline: 'none',
                      },
                      '&:focus-within': {
                        outline: 'none',
                      },
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      minHeight: { xs: 48, sm: 52 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.focus',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: 'background.default',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      minHeight: { xs: 48, sm: 56 },
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      backgroundColor: 'background.default',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      p: { xs: 1.5, sm: 2 },
                    },
                    '& .MuiDataGrid-actionsCell': {
                      gap: 0.5,
                    },
                  }}
                />
              </Box>
            </Card>
          )}

          {/* Floating Action Button - Mobile Only */}
          {isMobile && (
            <Fab
              color="primary"
              aria-label="add employee"
              sx={{
                position: 'fixed',
                bottom: 80,
                right: 16,
                boxShadow: 4,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onClick={handleCreateEmployee}
            >
              <Add />
            </Fab>
          )}
        </Box>
      )}

      {/* Attendance Tab Content */}
      {currentTab === 1 && (
        <AttendanceManagementPageFinal />
      )}

      {/* Calendar View Tab Content */}
      {currentTab === 2 && (
        <EmployeeCalendarView />
      )}

      {/* Holidays Tab Content */}
      {currentTab === 3 && (
        <HolidayList />
      )}

      {/* Dialogs */}
      <EmployeeDialog
        open={employeeDialogOpen}
        onClose={() => setEmployeeDialogOpen(false)}
        onSubmit={handleEmployeeSubmit}
        employee={selectedEmployee}
      />

      <EmployeeDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        employee={selectedEmployee}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete ${selectedEmployee?.full_name}? This action cannot be undone.`}
      />
    </Box>
  )
}
